import "@/App.css";
import { useState, useEffect, useRef } from "react";
import { Shield, Zap, BarChart3, Brain, Chrome, CheckCircle2, ChevronDown, ChevronUp, Eye } from "lucide-react";

// Demo states configuration
const DEMO_STATES = [
  { state: 'good', icon: '✅', label: 'Good', color: '#22c55e', tip: "All good! AI's memory is sharp.", messages: 3, scrollPercent: 0 },
  { state: 'fading', icon: '⚠️', label: 'Fading', color: '#eab308', tip: "Getting lengthy - maybe summarize soon?", messages: 8, scrollPercent: 0.25 },
  { state: 'risky', icon: '🔴', label: 'Risky', color: '#ef4444', tip: "Context is fading. Time for a fresh chat!", messages: 14, scrollPercent: 1 },
];

// ALL chat messages pre-loaded (never re-rendered, just scrolled)
const ALL_CHAT_MESSAGES = [
  { type: 'user', text: 'Can you help me understand quantum computing?' },
  { type: 'ai', text: 'Of course! Quantum computing uses quantum mechanics principles to process information in fundamentally new ways.' },
  { type: 'user', text: 'How does superposition work?' },
  { type: 'ai', text: 'Superposition allows qubits to exist in multiple states simultaneously, unlike classical bits.' },
  { type: 'user', text: 'What about entanglement?' },
  { type: 'ai', text: 'Entanglement links qubits so measuring one instantly affects the other, regardless of distance.' },
  { type: 'user', text: 'How is this used in practice?' },
  { type: 'ai', text: 'Applications include cryptography, drug discovery, and complex optimization problems.' },
  { type: 'user', text: 'Can you explain quantum gates?' },
  { type: 'ai', text: 'Quantum gates manipulate qubits similar to logic gates, but operate on probability amplitudes.' },
  { type: 'user', text: 'What companies are leading this?' },
  { type: 'ai', text: 'IBM, Google, Microsoft, and several startups are making significant progress in the field.' },
  { type: 'user', text: 'When will quantum computers be mainstream?' },
  { type: 'ai', text: 'Experts estimate practical quantum advantage could be achieved within the next decade.' },
];

// Conversation State Demo Component - Auto-animating with smooth scroll
const ConversationStateDemo = () => {
  const [stateIndex, setStateIndex] = useState(0);
  const chatContainerRef = useRef(null);
  const currentState = DEMO_STATES[stateIndex];

  // Auto-cycle through states every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setStateIndex((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Smooth scroll to position based on current state
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    // Small delay to ensure render is complete
    const scrollTimeout = setTimeout(() => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      const targetScroll = maxScroll * currentState.scrollPercent;
      
      container.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }, 100);
    
    return () => clearTimeout(scrollTimeout);
  }, [stateIndex, currentState.scrollPercent]);

  return (
    <div data-testid="conversation-state-demo">
      {/* Main demo container - responsive flex */}
      <div className="bg-[#1a1a1a] rounded-2xl border border-gray-800 overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          
          {/* LEFT: Chat mockup with browser chrome */}
          <div className="flex-1 p-5 border-b lg:border-b-0 lg:border-r border-gray-800">
            {/* Browser header */}
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-700">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
              </div>
              <div className="flex-1 ml-3">
                <div className="bg-gray-800 rounded px-3 py-1 text-xs text-gray-500 max-w-[180px]">
                  claude.ai/chat/...
                </div>
              </div>
            </div>

            {/* Chat messages - fixed container with smooth scroll animation */}
            <div 
              ref={chatContainerRef}
              className="space-y-3 h-[260px] overflow-hidden"
              style={{ scrollBehavior: 'smooth' }}
            >
              {ALL_CHAT_MESSAGES.map((msg, idx) => (
                <div 
                  key={idx} 
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`px-3 py-2 rounded-2xl max-w-[85%] text-sm ${
                    msg.type === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-md' 
                      : 'bg-gray-700 text-gray-200 rounded-bl-md'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {/* Typing indicator at the end */}
              <div className="flex justify-start">
                <div className="bg-gray-700 text-gray-400 px-3 py-2 rounded-2xl text-sm">
                  <span className="animate-pulse">●●●</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Status panel */}
          <div className="w-full lg:w-56 p-5 bg-[#151515] flex flex-col">
            {/* Current status with pulsing glow */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative">
                <div 
                  className="absolute inset-0 rounded-full animate-ping opacity-40"
                  style={{ backgroundColor: currentState.color }}
                />
                <div
                  className="relative w-12 h-12 rounded-full flex items-center justify-center text-xl transition-all duration-500"
                  style={{ 
                    backgroundColor: currentState.color,
                    boxShadow: `0 0 20px ${currentState.color}50`
                  }}
                >
                  {currentState.icon}
                </div>
              </div>
              <div>
                <span 
                  className="font-bold block text-base transition-colors duration-500" 
                  style={{ color: currentState.color }}
                >
                  {currentState.label}
                </span>
                <span className="text-gray-500 text-xs">{currentState.messages} messages</span>
              </div>
            </div>
            
            {/* Current tip */}
            <div className="bg-[#1a1a1a] rounded-lg p-3 text-sm text-gray-300 mb-5 border border-gray-800">
              💡 {currentState.tip}
            </div>
            
            {/* What the colors mean - STATIC */}
            <div className="mb-5">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">What the colors mean</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded-full bg-green-500 flex-shrink-0"></div>
                  <span className="text-gray-400">Good - AI remembers everything</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded-full bg-yellow-500 flex-shrink-0"></div>
                  <span className="text-gray-400">Fading - Getting long</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0"></div>
                  <span className="text-gray-400">Risky - Start fresh</span>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, color }) => (
  <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-700 hover:border-gray-500 transition-all hover:transform hover:scale-[1.02]" data-testid="feature-card">
    <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center mb-4`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-300 text-sm leading-relaxed">{description}</p>
  </div>
);

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-700 last:border-0">
      <button
        className="w-full py-4 flex items-center justify-between text-left hover:text-violet-400 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="faq-item"
      >
        <span className="text-white font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4 text-gray-300 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

// Installation Step Component
const InstallStep = ({ number, title, description }) => (
  <div className="flex gap-4" data-testid="install-step">
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center font-bold">
      {number}
    </div>
    <div>
      <h4 className="text-white font-medium mb-1">{title}</h4>
      <p className="text-gray-300 text-sm">{description}</p>
    </div>
  </div>
);

function App() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-lg border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/trianglebat-logo.png" alt="TriangleBat" className="h-16 w-auto object-contain" />
            <span className="text-xl font-bold">TriangleBat</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#features" className="text-gray-400 hover:text-white transition-colors text-sm">Features</a>
            <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors text-sm">How It Works</a>
            <button className="bg-violet-500/10 hover:bg-violet-500/20 text-violet-400 border border-violet-500/30 px-4 py-2 rounded-lg font-medium text-sm transition-all">
              Coming Soon
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse"></span>
                <span className="text-violet-400 text-sm font-medium">Your AI Session Companion</span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Know What<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                  AI Actually Remembers
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Like a bat using echolocation, TriangleBat senses the invisible. It tracks your conversation context locally and tells you when context is fading—before your responses start suffering.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#how-it-works"
                  className="bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center gap-3 transition-all border border-gray-800"
                >
                  How It Works
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 pt-8 border-t border-gray-700">
                <div className="flex items-center gap-2">
                  <Chrome className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-400 text-sm">Chrome Extension</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-400 text-sm">100% Local</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-400 text-sm">Always Ready</span>
                </div>
              </div>
            </div>
            <div className="lg:pl-8">
              <ConversationStateDemo />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - The Triangle */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0a0a0a] to-[#0f0f0f]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">The Invisible Problem</h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            AI doesn't tell you when it's overwhelmed. As conversations grow, three things break down:
          </p>
          <p className="text-violet-400 text-sm mb-8">
            TriangleBat's wings represent these three warning signs—always tracking them locally.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
              <div className="text-3xl mb-3">🔄</div>
              <h3 className="text-white font-semibold mb-2">Repetition</h3>
              <p className="text-gray-300 text-sm">AI forgets what was discussed and starts repeating itself</p>
            </div>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
              <div className="text-3xl mb-3">😵</div>
              <h3 className="text-white font-semibold mb-2">Confusion</h3>
              <p className="text-gray-300 text-sm">Earlier instructions get lost in the noise</p>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
              <div className="text-3xl mb-3">📉</div>
              <h3 className="text-white font-semibold mb-2">Decline</h3>
              <p className="text-gray-300 text-sm">Response quality degrades without warning</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Your Privacy-First Companion</h2>
            <p className="text-gray-300 text-lg">TriangleBat works locally, alerts you when it matters</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={Eye}
              title="Always Ready"
              description="Like a bat's echolocation, TriangleBat senses what you can't see—context overload building in your conversation."
              color="bg-purple-500"
            />
            <FeatureCard
              icon={BarChart3}
              title="Real-Time Conversation State"
              description="A simple color-coded indicator shows what AI can still access at a glance. Green to red, no guesswork."
              color="bg-green-500"
            />
            <FeatureCard
              icon={Brain}
              title="Quality-First Analysis"
              description="Smart algorithm prioritizes reliable signals. Message count matters more than fuzzy token math."
              color="bg-cyan-500"
            />
            <FeatureCard
              icon={Shield}
              title="100% Private"
              description="Everything runs locally in your browser. Your conversations never leave your device. Ever."
              color="bg-blue-500"
            />
            <FeatureCard
              icon={Zap}
              title="Lightweight"
              description="Runs silently in the background. No slowdowns, no interruptions—until you need to know."
              color="bg-yellow-500"
            />
            <FeatureCard
              icon={Chrome}
              title="Multi-Platform Support"
              description="Works with Anthropic (Claude), OpenAI (ChatGPT), Google (Gemini), and Perplexity.ai. One tiny companion preserving your session context."
              color="bg-orange-500"
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How TriangleBat Sees</h2>
            <p className="text-gray-300 text-lg">Simple, reliable signals—no black box magic</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <span className="text-violet-400 font-bold">60%</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">Message Count</h3>
                    <p className="text-gray-300 text-sm">The most reliable signal. More back-and-forth = more context strain. Simple math that works.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <span className="text-cyan-400 font-bold">40%</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">Token Estimate</h3>
                    <p className="text-gray-300 text-sm">Secondary signal based on ~750 tokens per message. A rough guide, not gospel.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-4 bg-violet-500/10 border border-violet-500/30 rounded-xl">
                <p className="text-sm text-gray-200">
                  <strong className="text-violet-400">Why this approach?</strong> Token counting is unreliable—message lengths vary wildly. Message count is consistent. TriangleBat trusts what it can actually see.
                </p>
              </div>
            </div>
            <div>
              <div className="bg-[#1a1a1a] rounded-2xl p-8 border border-gray-700">
                <h3 className="text-xl font-semibold mb-6">What TriangleBat Tells You</h3>
                <div className="space-y-4">
                  {[
                    { icon: "✅", status: "Good", color: "#22c55e", desc: "All good! AI's memory is sharp." },
                    { icon: "⚠️", status: "Fading", color: "#eab308", desc: "Getting long - AI might start forgetting." },
                    { icon: "🔴", status: "Risky", color: "#ef4444", desc: "Start fresh for best results!" },
                  ].map((item) => (
                    <div key={item.status} className="flex items-center gap-4">
                      <div
                        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-lg"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <span className="text-white font-medium block">{item.status}</span>
                        <p className="text-gray-300 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
                  <p className="text-sm text-gray-300">
                    <span className="text-violet-400 font-medium">Pro tip:</span> Upgrade to Pro for numeric scores (0-100), token counts, and detailed context metrics.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Section */}
      <section id="install" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Get Your Companion</h2>
            <p className="text-gray-300 text-lg">Up and running in under a minute</p>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-8 border border-gray-700 mb-8">
            <div className="space-y-8">
              <InstallStep
                number="1"
                title="Get the Extension"
                description="Contact us to get access to the TriangleBat extension. We'll provide you with the installation package."
              />
              <InstallStep
                number="2"
                title="Extract the Package"
                description="Extract the provided ZIP file to a folder on your computer."
              />
              <InstallStep
                number="3"
                title="Enable Developer Mode"
                description='Go to chrome://extensions and toggle "Developer mode" in the top right.'
              />
              <InstallStep
                number="4"
                title="Load Extension"
                description="Click 'Load unpacked' and select the extracted folder. Done!"
              />
            </div>
          </div>
          <div className="text-center">
            <p className="text-gray-400 text-sm">Works with Chrome, Brave, Edge, and other Chromium browsers</p>
            <p className="text-gray-500 text-xs mt-2">
              Contact us for access to the extension package
            </p>
          </div>
        </div>
      </section>

      {/* Pro Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0a0a0a] to-violet-950/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-violet-400 text-sm font-medium">✨ Pro Features</span>
          </div>
          <h2 className="text-4xl font-bold mb-4">TriangleBat Pro</h2>
          <p className="text-gray-300 text-lg mb-8">For power users who live in AI chats</p>
          <div className="grid md:grid-cols-2 gap-6 text-left max-w-2xl mx-auto">
            {[
              "Detailed state metrics",
              "Advanced metrics panel",
              "Accurate model detection",
              "Token & context tracking",
              "Copy Recap feature",
              "Session history",
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-violet-400 flex-shrink-0" />
                <span className="text-gray-200">{feature}</span>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <div className="inline-block bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/40 rounded-xl px-8 py-4">
              <span className="text-3xl font-bold text-white">TBD</span>
              <p className="text-gray-400 text-sm mt-1">Pricing coming soon</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Questions?</h2>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-700">
            <FAQItem
              question="Is my data sent anywhere?"
              answer="Never. TriangleBat runs entirely in your browser. Your conversations stay on your device—we can't see them even if we wanted to."
            />
            <FAQItem
              question="Which AI platforms work?"
              answer="TriangleBat supports Anthropic (Claude Sonnet 4, Claude Haiku 4.5), OpenAI (GPT-4o, GPT-4o Mini), Google (Gemini 2.0 Flash, Gemini 2.0 Pro), and Perplexity.ai. We're continuously adding support for more platforms and models."
            />
            <FAQItem
              question="How accurate is the conversation state?"
              answer="It's a practical estimate, not a scientific measurement. We prioritize message count because it's reliable—more back-and-forth means more context strain. The goal is 'useful signal' not 'perfect math.'"
            />
            <FAQItem
              question="Will it slow down my browser?"
              answer="Nope. TriangleBat only wakes up on AI chat sites and uses minimal resources. You won't notice it's there—until it helps you."
            />
            <FAQItem
              question="Why 'TriangleBat'?"
              answer="Our mascot is a friendly bat with triangular wings—representing the three warning signs of context overload: repetition, confusion, and quality decline. Like a bat using echolocation, we help you sense what's invisible."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-700">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center gap-6">
            {/* Mascot */}
            <img 
              src="/trianglebat-mascot.png" 
              alt="TriangleBat Mascot" 
              className="w-24 h-auto opacity-90 hover:opacity-100 transition-opacity"
            />
            
            {/* Brand */}
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold">TriangleBat</span>
              <span className="text-gray-500 text-sm">v0.3.2</span>
            </div>
            
            <p className="text-gray-400 text-sm">
              Keeping your context safe and local
            </p>
            
            {/* Links */}
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy</a>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors text-sm">Terms</a>
              <a href="#contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
